import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useModalBackButton } from "@/hooks/useModalBackButton";
import { useOfflineProducts } from "@/hooks/useOfflineProducts";
import ImportListInput from "./ImportListInput";
import ImportListTable from "./ImportListTable";
import { ImportMatchItem } from "./import-list-types";
import { UserProduct } from "@/types";
import { addShoppingItem } from "@/services/shopping/items";
import CreateProductsDialog from "./CreateProductsDialog";
import { useQueryClient } from "@tanstack/react-query";
import { safeToastError, safeToastSuccess } from "@/components/ui/toastManager";
import { parseImportText } from "./parseImportText";
import { validateQuantity } from "@/utils/security";

interface ImportListDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
}

/**
 * Modale che permette di incollare un elenco di prodotti e abbinarli a quelli
 * esistenti. Eventuali articoli non trovati possono essere creati tramite il
 * flusso "Crea Nuovo".
 *
 * @param props Controllo di apertura e identificativo della lista
 * @returns Finestra di dialogo pronta all'uso
 */
const ImportListDialog = ({ isOpen, onOpenChange, listId }: ImportListDialogProps) => {
  const [text, setText] = useState("");
  const [rows, setRows] = useState<ImportMatchItem[]>([]);
  const { theme } = useThemeConfig();
  const { products } = useOfflineProducts();
  const { handleOpenChange } = useModalBackButton({ isOpen, modalName: "import-list-dialog", onOpenChange });
  const queryClient = useQueryClient();
  const [createRows, setCreateRows] = useState<ImportMatchItem[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [existingRows, setExistingRows] = useState<ImportMatchItem[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setText("");
      setRows([]);
      setExistingRows([]);
    }
  }, [isOpen]);

  const handleParse = () => {
    try {
      const items = parseImportText(text);
      if (items.length === 0) {
        safeToastError("Nessun prodotto valido da importare");
        return;
      }
      // Pre-seleziona un match esistente per ridurre il lavoro manuale
      const mapped = items.map(orig => {
        const match = products.find((p: UserProduct) => p.name.toLowerCase() === orig.toLowerCase());
        return { original: orig, selectedId: match ? match.id : null };
      });
      setRows(mapped);
    } catch (e) {
      safeToastError("Errore durante il parsing della lista");
    }
  };

  const updateRow = (i: number, v: string | null) => {
    setRows(prev => prev.map((r, idx) => (idx === i ? { ...r, selectedId: v } : r)));
  };

  const handleImport = async () => {
    try {
      const existing = rows.filter(r => r.selectedId);
      const toCreate = rows.filter(r => !r.selectedId);

      setExistingRows(existing);

      if (toCreate.length > 0) {
        setCreateRows(toCreate);
        setIsCreateDialogOpen(true);
      } else {
        await handleCreateComplete();
      }
    } catch (e) {
      safeToastError("Errore durante l'importazione");
    }
  };

  const handleCreateComplete = async () => {
    try {
      setIsCreateDialogOpen(false);

      if (existingRows.length > 0) {
        await Promise.all(
          existingRows.map(r => {
            const p = products.find(pr => pr.id === r.selectedId);
            const quantityResult = validateQuantity('1');

            if (!quantityResult.isValid) {
              safeToastError("Quantità non valida per l'articolo importato");
              return Promise.resolve();
            }

            return addShoppingItem(listId, {
              product_id: r.selectedId!,
              quantity: quantityResult.sanitized,
              unit: p?.defaultUnit ?? "",
              category: p?.categoryId ?? "other",
            });
          })
        );
        setExistingRows([]);
      }

      await queryClient.invalidateQueries({ queryKey: ['shopping-items', listId] });
      safeToastSuccess("Importazione completata");
      handleOpenChange(false);
    } catch (e) {
      safeToastError("Errore durante l'importazione");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="min-h-[45vh]" // Altezza allineata alle altre modali di creazione/modifica
        containerClassName="h-full overflow-y-auto"
        style={{
          borderRadius: theme.borderRadius || "0.5rem",
          backgroundColor: theme.cardBackgroundColor || theme.backgroundColor,
          color: theme.sectionTextColor || theme.textColor,
        }}
      >
        <DialogHeader className="space-y-2 pb-4">
          <DialogTitle>Importa prodotti</DialogTitle>
          <DialogDescription style={{ color: theme.sectionTitleColor || "#000000", textAlign: "justify" }}>
            Inserisci un elenco di prodotti separati da virgola (,), punto e virgola (;) o andando a capo. Eventuali elenchi
            puntati o numerati verranno interpretati automaticamente. Per i prodotti non trovati verrà proposta la creazione
            di un nuovo prodotto e richiesta la categoria di appartenenza.
          </DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? (
          <ImportListInput text={text} setText={setText} onParse={handleParse} onCancel={() => handleOpenChange(false)} />
        ) : (
          <ImportListTable rows={rows} products={products} onChange={updateRow} onExecute={handleImport} />
        )}
      </DialogContent>
      <CreateProductsDialog
        open={isCreateDialogOpen}
        items={createRows}
        listId={listId}
        onComplete={handleCreateComplete}
        onCancel={() => {
          setIsCreateDialogOpen(false);
          handleOpenChange(false);
        }}
      />
    </Dialog>
  );
};

export default ImportListDialog;
